import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { iLoginFormValues } from "../../components/Form/LoginForm/types";
import { iRegisterFormValues } from "../../components/Form/RegisterForm/types";
import { api } from "../../services";
import { MessageToast } from "../../style/messageToast";
import {
  iFilterProductsProps,
  iLoginRequest,
  iProductsProps,
  iRegisterRequest,
  iUserContext,
  iUserProps,
  iUserProviderProps,
} from "./types";

export const UserContext = createContext({} as iUserContext);

export const UserProvider = ({ children }: iUserProviderProps) => {
  const [globalLoading, setGlobalLoading] = useState(false);
  const [user, setUser] = useState<iUserProps | null>(null);
  const [filterProducts, setFilterProducts] = useState(
    [] as iFilterProductsProps[]
  );
  const [products, setProducts] = useState([] as iProductsProps[]);
  const token = localStorage.getItem("@tokenUser");
  const navigate = useNavigate();
  const [showInputSearch, setShowInputSearch] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        return;
      }
      try {
        setGlobalLoading(true);
        const request = await api.get("products", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProducts(request.data);
        navigate("user");
      } catch (error) {
        localStorage.clear();
        setUser(null);
      } finally {
        setGlobalLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const userLogin = async (
    data: iLoginFormValues,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      setLoading(true);
      const request = await toast.promise(
        api.post<iLoginRequest>("login", data),
        {
          pending: {
            render() {
              return <MessageToast message="Carregando..." />;
            },
            icon: true,
          },
          success: {
            render({ data }: any) {
              return (
                <MessageToast message={`Bem-vindo ${data?.data.user.name}!`} />
              );
            },
            icon: true,
          },
          error: {
            render({ data }: any) {
              console.log(data);
              return <MessageToast message={data.response.data} />;
            },
            icon: true,
          },
        }
      );
      if (request.statusText === "OK") {
        setUser(request.data.user);
        localStorage.setItem("@tokenUser", request.data.accessToken);
        navigate("user");
      }
    } catch (error) {
      return error;
    } finally {
      setLoading(false);
    }
  };

  const userRegister = async (
    data: iRegisterFormValues,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      setLoading(true);
      const request = await toast.promise(
        api.post<iRegisterRequest>("users", data),
        {
          pending: {
            render() {
              return <MessageToast message="Carregando..." />;
            },
            icon: true,
          },
          success: {
            render({ data }: any) {
              return (
                <MessageToast
                  message={`${data.data.user.name} conta criada com sucesso!`}
                />
              );
            },
            icon: true,
          },
          error: {
            render({ data }: any) {
              return <MessageToast message={data.response.data} />;
            },
            icon: true,
          },
        }
      );
      navigate("");
      return request.data;
    } catch (error) {
      return error;
    } finally {
      setLoading(false);
    }
  };

  const userLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate("");
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userLogin,
        globalLoading,
        userLogout,
        userRegister,
        setFilterProducts,
        products,
        filterProducts,
        token,
        showInputSearch,
        setShowInputSearch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
